import { CSSProperties, ReactNode, useState, useRef, MouseEvent } from 'react';
import ColorPickerInput from 'elements/ColorPickerInput';
import ModalSymphony from 'elements/ModalSymphony';
import useWebsiteBuilder from 'Hooks/useWebsiteBuilder';
import Sty from 'modules/Sty';
import { clsx, styx } from 'modules/Utils';
import SwitchSymphony, { SwitchType } from 'elements/SwitchSymphony';
import MultiPalette from 'elements/MultiPalette';
import ButtonSymphony from 'elements/ButtonSymphony';
import UpgradeToUnlockCTA from 'pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA';
import { PlanType } from 'modules/Const';
import useBrand from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import { Popover } from '@mui/material';
import WebsiteBuidlerRequiresPaidPlan from './WebsiteBuidlerRequiresPaidPlan';

const COLOR_PICKER_WIDTH = 120;

enum WebsiteBuilderColorType {
    BACKGROUND_COLOR = 'backgroundColor',
    SECONDARY_COLOR = 'secondaryColor',
    TEXT_COLOR = 'textColor',
    TEXT_SECONDARY_COLOR = 'textSecondaryColor',
}

interface WebsiteBuilderFooterProps {
    className?: string;
    contentIndex: number;
    styleOptions?: ReactNode;
    isBackgroundColor?: boolean;
    isSecondaryColor?: boolean;
    isTextColor?: boolean;
    isTextSecondaryColor?: boolean;
    isBorder?: boolean;

    requiresPaidPlan?: boolean;
    titleRequired?: string;
}

const WebsiteBuilderFooter = ({
    className,
    contentIndex,
    styleOptions,
    isBackgroundColor = false,
    isSecondaryColor = false,
    isTextColor = false,
    isTextSecondaryColor = false,
    isBorder = true,
    requiresPaidPlan = false,
    titleRequired,
}: WebsiteBuilderFooterProps) => {
    const {
        isPaidBrand,
        isProBrand,
    } = useBrand();

    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder();

    const canUseCustomColors = isPaidBrand

    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentSecondaryColor = websiteContent[contentIndex].contentSecondaryColor;
    const isContentSecondaryColorEnabled = websiteContent[contentIndex].isContentSecondaryColorEnabled;

    const contentTextColor = websiteContent[contentIndex].contentTextColor;
    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextSecondaryColor = websiteContent[contentIndex].contentTextSecondaryColor;
    const isContentTextSecondaryColorEnabled = websiteContent[contentIndex].isContentTextSecondaryColorEnabled;

    const contentType = websiteContent[contentIndex].type;

    const [isCustomizeStyle, setIsCustomizeStyle] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [colorType, setColorType] = useState<WebsiteBuilderColorType>(WebsiteBuilderColorType.BACKGROUND_COLOR);

    const contentRef = useRef<HTMLDivElement>(null);
    const backgroundColorRef = useRef<HTMLDivElement>(null);
    const secondaryColorRef = useRef<HTMLDivElement>(null);
    const textColorRef = useRef<HTMLDivElement>(null);
    const textSecondaryColorRef = useRef<HTMLDivElement>(null);

    /** Closes the color picker popover */
    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleSelectBackgroundColor = (color: string) => {
        const newContent = [...websiteContent];
        newContent[contentIndex].contentBackgroundColor = color;
        newContent[contentIndex].isContentBackgroundColorEnabled = true;
        setWebsiteContent(newContent);
    }

    const handleSelectSecondaryColor = (color: string) => {
        const newContent = [...websiteContent];
        newContent[contentIndex].contentSecondaryColor = color;
        newContent[contentIndex].isContentSecondaryColorEnabled = true;
        setWebsiteContent(newContent);
    }

    const handleSelectTextColor = (color: string) => {
        const newContent = [...websiteContent];
        newContent[contentIndex].contentTextColor = color;
        newContent[contentIndex].isContentTextColorEnabled = true;
        setWebsiteContent(newContent);
    }

    const handleSelectTextSecondaryColor = (color: string) => {
        const newContent = [...websiteContent];
        newContent[contentIndex].contentTextSecondaryColor = color;
        newContent[contentIndex].isContentTextSecondaryColorEnabled = true;
        setWebsiteContent(newContent);
    }

    const isCustomizeColorSelected = () => {
        return Boolean(
            isContentBackgroundColorEnabled ||
            isContentSecondaryColorEnabled ||
            isContentTextColorEnabled ||
            isContentTextSecondaryColorEnabled
        );
    }

    const renderBackgroundColorPicker = () => {
        return (
            <div
                className="websiteBuilderHeaderColorPickerContainer"
                style={sty.websiteBuilderHeaderColorPickerContainer}
                ref={backgroundColorRef}
            >
                <ColorPickerInput
                    className="colorPickerInput"
                    selectedColor={contentBackgroundColor || '#FFFFFF'}
                    selectColor={handleSelectBackgroundColor}
                    style={{
                        opacity: isContentBackgroundColorEnabled ? 1 : 0.3,
                    }}
                    onClick={() => {
                        if (backgroundColorRef.current) {
                            setAnchorEl(backgroundColorRef.current);
                            setColorType(WebsiteBuilderColorType.BACKGROUND_COLOR);
                        }
                    }}
                />
                <SwitchSymphony
                    className="colorPickerSwitch"
                    type={SwitchType.SWITCH}
                    checked={websiteContent[contentIndex].isContentBackgroundColorEnabled}
                    onChange={(value) => {
                        const newContent = [...websiteContent];
                        newContent[contentIndex].isContentBackgroundColorEnabled = value;
                        setWebsiteContent(newContent);
                    }}
                />
            </div>
        )
    }

    const renderSecondaryColorPicker = () => {
        return (
            <div
                className="websiteBuilderHeaderColorPickerContainer"
                style={sty.websiteBuilderHeaderColorPickerContainer}
                ref={secondaryColorRef}
            >
                <ColorPickerInput
                    className="colorPickerInput"
                    selectedColor={contentSecondaryColor || '#FFFFFF'}
                    selectColor={handleSelectSecondaryColor}
                    style={{
                        opacity: isContentSecondaryColorEnabled ? 1 : 0.3,
                    }}
                    onClick={() => {
                        if (secondaryColorRef.current) {
                            setAnchorEl(secondaryColorRef.current);
                            setColorType(WebsiteBuilderColorType.SECONDARY_COLOR);
                        }
                    }}
                />
                <SwitchSymphony
                    className="colorPickerSwitch"
                    type={SwitchType.SWITCH}
                    checked={websiteContent[contentIndex].isContentSecondaryColorEnabled}
                    onChange={(value) => {
                        const newContent = [...websiteContent];
                        newContent[contentIndex].isContentSecondaryColorEnabled = value;
                        setWebsiteContent(newContent);
                    }}
                />
            </div>
        )
    }

    const renderTextColorPicker = () => {
        return (
            <div
                className="websiteBuilderHeaderColorPickerContainer"
                style={sty.websiteBuilderHeaderColorPickerContainer}
                ref={textColorRef}
            >
                <ColorPickerInput
                    className="colorPickerInput"
                    selectedColor={contentTextColor || '#FFFFFF'}
                    selectColor={handleSelectTextColor}
                    style={{
                        opacity: isContentTextColorEnabled ? 1 : 0.3,
                    }}
                    onClick={() => {
                        if (textColorRef.current) {
                            setAnchorEl(textColorRef.current);
                            setColorType(WebsiteBuilderColorType.TEXT_COLOR);
                        }
                    }}
                />
                <SwitchSymphony
                    className="colorPickerSwitch"
                    type={SwitchType.SWITCH}
                    checked={isContentTextColorEnabled}
                    onChange={(value) => {
                        const newContent = [...websiteContent];
                        newContent[contentIndex].isContentTextColorEnabled = value;
                        setWebsiteContent(newContent);
                    }}
                />
            </div>
        )
    }

    const renderTextSecondaryColorPicker = () => {
        return (
            <div
                className="websiteBuilderHeaderColorPickerContainer"
                style={sty.websiteBuilderHeaderColorPickerContainer}
                ref={textSecondaryColorRef}
            >
                <ColorPickerInput
                    className="colorPickerInput"
                    selectedColor={contentTextSecondaryColor || '#FFFFFF'}
                    selectColor={handleSelectTextSecondaryColor}
                    style={{
                        opacity: isContentTextSecondaryColorEnabled ? 1 : 0.3,
                    }}
                    onClick={() => {
                        if (textSecondaryColorRef.current) {
                            setAnchorEl(textSecondaryColorRef.current);
                            setColorType(WebsiteBuilderColorType.TEXT_SECONDARY_COLOR);
                        }
                    }}
                />
                <SwitchSymphony
                    className="colorPickerSwitch"
                    type={SwitchType.SWITCH}
                    checked={isContentTextSecondaryColorEnabled}
                    onChange={(value) => {
                        const newContent = [...websiteContent];
                        newContent[contentIndex].isContentTextSecondaryColorEnabled = value;
                        setWebsiteContent(newContent);
                    }}
                />
            </div>
        )
    }


    const renderCta = () => {
        return (
            <UpgradeToUnlockCTA
                className="upgradeToUnlockCTAWebsiteBuilderFooter"
                style={{
                    marginTop: 16,
                }}
                highlightedPlan={PlanType.LITE}
                requiresPaidPlan={!canUseCustomColors && (requiresPaidPlan || isCustomizeColorSelected())}
                upgradeSource={`Website Builder - ${contentType}`}
                buttonText="Upgrade to unlock Premium Themes"
                upgradePopupCallToAction="Unlock unlimited Website Customization with Symphony Lite"
                paywallText="You've selected a Lite-only theme. You'll need to upgrade to Lite before publishing your site."
            />
        )
    }

    return (
        <>
            <div
                className={clsx("websiteBuilderFooterComponent", className)}
                style={styx(sty.websiteBuilderFooterComponent, {
                    borderTop: isBorder ? Sty.border : 'none',
                })}
            >
                {styleOptions &&
                    <div
                        className="footerRowContainer"
                        style={sty.footerRowContainer}
                    >
                        <div style={Sty.text16grey}>
                            Style
                        </div>
                        {styleOptions}
                    </div>
                }
                <div
                    className="customizeStyleButtonContainer"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 24,
                    }}
                >
                    <ButtonSymphony
                        className="customizeStyleButton"
                        onClick={() => {
                            setIsCustomizeStyle(!isCustomizeStyle)
                        }}
                        iconLeft="wand"
                        iconRight={
                            isCustomizeStyle ? 'chevronUp' : 'chevronDown'
                        }
                        color="black"
                        variant="text"
                        iconLeftOffset={-4}
                        iconRightOffset={-2}
                        iconLeftSize={22}
                    >
                        Customize Colors
                    </ButtonSymphony>
                    {isCustomizeColorSelected() && !canUseCustomColors &&
                        <div className="footerLiteText" style={Sty.text14purple}>
                            Custom Colors Require a Symphony Lite Plan
                        </div>
                    }
                </div>
                <div
                    className="animatedContentContainer"
                    style={styx(sty.animatedContentContainer, {
                        maxHeight: isCustomizeStyle ? 500 : 0,
                        opacity: isCustomizeStyle ? 1 : 0,
                        marginTop: isCustomizeStyle ? 16 : 0,
                    })}
                >

                    <div
                        className="websiteBuilderFooterRef"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}
                        ref={contentRef}
                    >
                        {isBackgroundColor &&
                            <>
                                <div
                                    className="footerRowContainer"
                                    style={sty.footerRowContainer}
                                >
                                    <div style={{ ...Sty.text16grey, minWidth: COLOR_PICKER_WIDTH }}>
                                        Background
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                        }}
                                    >
                                        {renderBackgroundColorPicker()}
                                    </div>
                                </div>
                                {isSecondaryColor &&
                                    <div
                                        className="footerRowContainer"
                                        style={sty.footerRowContainer}
                                    >
                                        <div style={{ ...Sty.text16grey, minWidth: COLOR_PICKER_WIDTH }}>
                                            Buttons
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 16,
                                            }}
                                        >
                                            {renderSecondaryColorPicker()}
                                        </div>
                                    </div>
                                }
                            </>
                        }

                        {isTextColor &&
                            <>
                                <div
                                    className="footerRowContainer"
                                    style={sty.footerRowContainer}
                                >
                                    <div style={{ ...Sty.text16grey, minWidth: COLOR_PICKER_WIDTH }}>
                                        Text 1
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                        }}
                                    >
                                        {renderTextColorPicker()}
                                    </div>
                                </div>
                                {isTextSecondaryColor &&
                                    <div
                                        className="footerRowContainer"
                                        style={sty.footerRowContainer}
                                    >
                                        <div style={{ ...Sty.text16grey, minWidth: COLOR_PICKER_WIDTH }}>
                                            Text 2
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 16,
                                            }}
                                        >
                                            {renderTextSecondaryColorPicker()}
                                        </div>
                                    </div>
                                }
                            </>
                        }
                    </div>
                </div>
                {renderCta()}
                <WebsiteBuidlerRequiresPaidPlan
                    className="websiteBuilderFooterRequiresPaidPlan"
                    style={{
                        marginTop: 16,
                    }}
                    requiresPaidPlan={requiresPaidPlan}
                    contentIndex={contentIndex}
                    title={titleRequired}
                />
            </div>

            {/* POPOVERS */}
            <Popover
                className="websiteBuilderColorPickerModal"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    }
                }}
                slotProps={{
                    root: {
                        style: {
                            zIndex: 1300,
                        }
                    }
                }}
            >
                <div className="colorPickerModalContent" style={sty.colorPickerModalContent}>
                    <MultiPalette
                        className="multiPalette"
                        style={sty.multiPalette}
                        selectedColor={
                            colorType === WebsiteBuilderColorType.BACKGROUND_COLOR ? contentBackgroundColor || '#FFFFFF' :
                                colorType === WebsiteBuilderColorType.SECONDARY_COLOR ? contentSecondaryColor || '#FFFFFF' :
                                    colorType === WebsiteBuilderColorType.TEXT_COLOR ? contentTextColor || '#FFFFFF' :
                                        contentTextSecondaryColor || '#FFFFFF'
                        }
                        selectColor={(color) => {
                            if (colorType === WebsiteBuilderColorType.BACKGROUND_COLOR) {
                                handleSelectBackgroundColor(color);
                            } else if (colorType === WebsiteBuilderColorType.SECONDARY_COLOR) {
                                handleSelectSecondaryColor(color);
                            } else if (colorType === WebsiteBuilderColorType.TEXT_COLOR) {
                                handleSelectTextColor(color);
                            } else {
                                handleSelectTextSecondaryColor(color);
                            }
                            // handleClosePopover();
                        }}
                        showBlackAndWhite={false}
                        size={32}
                    />
                </div>
            </Popover>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    websiteBuilderFooterComponent: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 16,
    },
    footerRowContainer: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 16,
    },
    animatedContentContainer: {
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
    },
    multiPalette: {
        width: '100%',
    },
    colorPickerModalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
    },
    colorPickerModalHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    colorPickerModalTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 600,
    },
    colorPickerModalSubtitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 400,
        color: '#666',
    },
    websiteBuilderHeaderColorPickerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
}

export default WebsiteBuilderFooter;